/*
 * Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
 * This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
 * The receipt or possession of this program does not convey any rights to use, reproduce or disclose
 * its contents in whole or in part, without the specific written consent of Inovalon, Inc.
 * Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
 * is a violation of the copyright laws and may subject you to criminal prosecution.
 */
import { container } from '../arc/configuration/ServiceLocator'
import { BaseSettingsManager, ISettingsManagerSetting } from '../arc/configuration/SettingsManager'
import { ITopNavBarApiClient } from '../services/TopNavBarApiClient/contracts'
import { MockTopNavBarApiClient } from '../services/TopNavBarApiClient/MockTopNavBarApiClient'
import { TopNavBarApiClient } from '../services/TopNavBarApiClient/TopNavBarApiClient'

import { ICoverageDiscoveryRequestApiClient } from '../services/CoverageDiscoveryRequestApiClient/contracts'
import { MockCoverageDiscoveryRequestApiClient } from '../services/CoverageDiscoveryRequestApiClient/MockCoverageDiscoveryRequestApiClient'
import { CoverageDiscoveryRequestApiClient } from '../services/CoverageDiscoveryRequestApiClient/CoverageDiscoveryRequestApiClient'
import { ICoverageDiscoveryRequestStateManager } from '../services/CoverageDiscoveryRequestStateManager/contracts'
import { CoverageDiscoveryRequestStateManager } from '../services/CoverageDiscoveryRequestStateManager/CoverageDiscoveryRequestStateManager'

import { ICoverageDiscoveryResponseApiClient } from '../services/CoverageDiscoveryResponseApiClient/contracts'
import { MockCoverageDiscoveryResponseApiClient } from '../services/CoverageDiscoveryResponseApiClient/MockCoverageDiscoveryResponseApiClient'
import { CoverageDiscoveryResponseApiClient } from '../services/CoverageDiscoveryResponseApiClient/CoverageDiscoveryResponseApiClient'
import { ICoverageDiscoveryResponseStateManager } from '../services/CoverageDiscoveryResponseStateManager/contracts'
import { CoverageDiscoveryResponseStateManager } from '../services/CoverageDiscoveryResponseStateManager/CoverageDiscoveryResponseStateManager'

import { IRequestHistoryApiClient } from '../services/RequestHistoryApiClient/contracts'
import { MockRequestHistoryApiClient } from '../services/RequestHistoryApiClient/MockRequestHistoryApiClient'
import { RequestHistoryApiClient } from '../services/RequestHistoryApiClient/RequestHistoryApiClient'
import { IRequestHistoryStateManager } from '../services/RequestHistoryStateManager/contracts'
import { RequestHistoryStateManager } from '../services/RequestHistoryStateManager/RequestHistoryStateManager'

import { IBatchesApiClient } from '../services/BatchesApiClient/contracts'
import { MockBatchesApiClient } from '../services/BatchesApiClient/MockBatchesApiClient'
import { BatchesApiClient } from '../services/BatchesApiClient/BatchesApiClient'
import { IBatchesStateManager } from '../services/BatchesStateManager/contracts'
import { BatchesStateManager } from '../services/BatchesStateManager/BatchesStateManager'

import { IAccountFacilityApiClient } from '../services/AccountFacilityApiClient/contracts'
import { MockAccountFacilityApiClient } from '../services/AccountFacilityApiClient/MockAccountFacilityApiClient'
import { AccountFacilityApiClient } from '../services/AccountFacilityApiClient/AccountFacilityApiClient'
import { IAccountFacilityGridStateManager } from '../services/AccountFacilityStateManager/contracts'
import { AccountFacilityStateManager } from '../services/AccountFacilityStateManager/AccountFacilityStateManager'

import { IExportRequestApiClient } from '../services/ExportRequestApiClient/contracts'
import { MockExportRequestApiClient } from '../services/ExportRequestApiClient/MockExportRequestApiClient'
import { ExportRequestApiClient } from '../services/ExportRequestApiClient/ExportRequestApiClient'

import { IViewReportApiClient } from '../services/ViewReportApiClient/contracts'
import { MockViewReportApiClient } from '../services/ViewReportApiClient/MockViewReportApiClient'
import { ViewReportApiClient } from '../services/ViewReportApiClient/ViewReportApiClient'

import { IPayersApiClient } from '../services/PayersApiClient/contracts'
import { MockPayersApiClient } from '../services/PayersApiClient/MockPayersApiClient'
import { PayersApiClient } from '../services/PayersApiClient/PayersApiClient'

import { ITransactionStatusApiClient } from '../services/TransactionStatusApiClient/contracts'
import { MockTransactionStatusApiClient } from '../services/TransactionStatusApiClient/MockTransactionStatusApiClient'
import { TransactionStatusApiClient } from '../services/TransactionStatusApiClient/TransactionStatusApiClient'

import { IEligibilityStatusApiClient } from '../services/EligibilityStatusApiClient/contracts'
import { MockEligibilityStatusApiClient } from '../services/EligibilityStatusApiClient/MockEligibilityStatusApiClient'
import { EligibilityStatusApiClient } from '../services/EligibilityStatusApiClient/EligibilityStatusApiClient'

import { IImportBatchUIStateManager } from '../services/ImportBatchUIStateManager/contracts'
import { IImportBatchUIApiClient } from '../services/ImportBatchUIApiClient/contracts'
import { ImportBatchUIStateManager } from '../services/ImportBatchUIStateManager/ImportBatchUIStateManager'
import { ImportBatchUIClient } from '../services/ImportBatchUIApiClient/ImportBatchUIApiClient'
import { MockImportBatchUIApiClient } from '../services/ImportBatchUIApiClient/MockImportBatchUIApiClient'

import jwtDecode from 'jwt-decode'

import { ICoverageDiscoveryTransactionApiClient } from '../services/CoverageDiscoveryTransactionApiClient/contracts'
import { MockCoverageDiscoveryTransactionApiClient } from '../services/CoverageDiscoveryTransactionApiClient/MockCoverageDiscoveryTransactionApiClient'
import { CoverageDiscoveryTransactionApiClient } from '../services/CoverageDiscoveryTransactionApiClient/CoverageDiscoveryTransactionApiClient'
import { IStateCodeApiClient } from '../services/StateCodeApiClient/contracts'
import { MockStateCodeApiClient } from '../services/StateCodeApiClient/MockStateCodeApiClient'
import { StateCodeApiClient } from '../services/StateCodeApiClient/StateCodeApiClient'
import { ICoverageDiscoveryTransactionStateManager } from '../services/CoverageDiscoveryTransactionStateManager/contracts'
import { CoverageDiscoveryTransactionStateManager } from '../services/CoverageDiscoveryTransactionStateManager/CoverageDiscoveryTransactionStateManager'
/**
 * This module centralizes all configurable application settings into a single public object with documentation
 * Settings are loaded on demand through strongly-typed properties
 * Settings can be configured at build-time via use of .env files or at startup-time via appConfig.js
 */

/**
 * the class for the singleton SettingsManager instance
 *
 * @class SettingsManager
 */
export class SettingsManager extends BaseSettingsManager {
	static REACT_APP_SSO_TOKEN_NAME = 'coveragediscovery.ssoToken'
	static REACT_APP_SESSION_TOKEN_NAME = 'coveragediscovery.sessionToken'
	static REACT_APP_IFRAME_PAGE_URLS = ''
	static SHOULD_LOAD_MENU = true
	static COVERAGE_DISCOVERY_TRANSACTION_EXPORT_LIMIT = 500
	static COVERAGE_DISCOVERY_TRANSACTION_EXPORT_MAXLIMIT = 10000
	static COVERAGE_DISCOVERY_BATCHES_INTERVAL_IN_MILLISECONDS = 10000
	static PRODUCT_PERMISSION_FOR_FACILITY_CONFIGURATION = 10011
	static DATE_REGULAR_EXPRESSION = /(^(((0?[1-9]|1[012])[-/.](0?[1-9]|1[0-9]|2[0-8]))|((0?[13578]|1[02])[-/.](29|30|31))|((0?[4,6,9]|11)[-/.](29|30)))[-/.](19|[2-9][0-9])\d\d$)|(^0?2[-/.]29[-/.](19|[2-9][0-9])(00|04|08|12|16|20|24|28|32|36|40|44|48|52|56|60|64|68|72|76|80|84|88|92|96)$)/
	static HospiceEpisodeDisplayDate = new Date('2020-10-01')
	static SECTION_STAY = 'sectionStay'
	static SECTION_PREVENTATIVE = 'sectionPreventative'
	static SECTION_STATUSALERTS = 'sectionStatusAlerts'
	static SECTION_PCPALERTS = 'sectionPCP'
	static SECTION_MCOSTATUSALERT = 'sectionMCOStatusAlert'
	static SECTION_OTHERPAYERALERT = 'sectionOtherPayer'
	static SECTION_MEDICARE_COMMERCIAL_SERVICECOVERAGE = 'sectionServiceCoverage'
	static SECTION_MEDICARE_INPATIENT = 'sectionInpatient'
	static SECTION_MEDICARE_HOMEHOSPICE = 'sectionHomeHospice'
	static SECTION_MEDICARE_THERAPYCAP = 'sectionTherapyCap'
	static SECTION_MEDICARE_ELIGIBILITY = 'sectionEligibility'
	static SECTION_MEDICARE_STATUSALERT = 'sectionStatusAlerts'
	static SECTION_MEDICARE_SERVICETYPE = 'sectionServiceType'
	static SECTION_MEDICARE_COVID19IMMUNIZATIONSECTION = 'sectionImmunization'
	static SECTION_CELIGIBILITY = 'sectionCEligibility'
	static SECTION_BENEFITINFORMATION = 'sectionBenefitInformation'
	static SECTION_CSERVICETYPE = 'sectionCServiceType'

	static COVERAGE_DISCOVERY_RESPONSE_INTERVAL = 5000
	static COVERAGE_DISCOVERY_TOTAL_POLLING_INTERVAL = 120000
	static RESPONSE_PAGE_TAB_PAYERNAME_MAXLENGTH = 45
	static RESPONSE_PAGE_MAX_PAYERS_TO_SHOW = 3
	static RESPONSE_PAGE_MAX_PAYERS_RANK_TO_SHOW = 3

	static REACT_APP_SUPPORT_SITE_BASE_URL = (): ISettingsManagerSetting<string> => BaseSettingsManager.getSettingAsString('REACT_APP_SUPPORT_SITE_BASE_URL', 'The support site base url', '/Auth/OnlineHelpLink?articleUrl=/help/pages/coveragediscovery/')
	static REACT_APP_SUPPORT_SITE_REQUEST_PAGE = (): ISettingsManagerSetting<string> => BaseSettingsManager.getSettingAsString('REACT_APP_SUPPORT_SITE_REQUEST_PAGE', 'The support site url for the request page', SettingsManager.REACT_APP_SUPPORT_SITE_BASE_URL().value + 'Request')
	static REACT_APP_SUPPORT_SITE_RESPONSE_PAGE = (): ISettingsManagerSetting<string> => BaseSettingsManager.getSettingAsString('REACT_APP_SUPPORT_SITE_RESPONSE_PAGE', 'The support site url for the response page', SettingsManager.REACT_APP_SUPPORT_SITE_BASE_URL().value + 'Response')
	static REACT_APP_SUPPORT_SITE_HISTORY_PAGE = (): ISettingsManagerSetting<string> => BaseSettingsManager.getSettingAsString('REACT_APP_SUPPORT_SITE_HISTORY_PAGE', 'The support site url for the history page', SettingsManager.REACT_APP_SUPPORT_SITE_BASE_URL().value + 'History')
	static REACT_APP_SUPPORT_SITE_BATCHES_PAGE = (): ISettingsManagerSetting<string> => BaseSettingsManager.getSettingAsString('REACT_APP_SUPPORT_SITE_BATCHES_PAGE', 'The support site url for the batches page', SettingsManager.REACT_APP_SUPPORT_SITE_BASE_URL().value + 'Batches')
	static REACT_APP_SUPPORT_SITE_FACILITIES_PAGE = (): ISettingsManagerSetting<string> => BaseSettingsManager.getSettingAsString('REACT_APP_SUPPORT_SITE_FACILITIES_PAGE', 'The support site url for the facilities page', SettingsManager.REACT_APP_SUPPORT_SITE_BASE_URL().value + 'Facilities')
	/**
	 * register all injectables to an ioc container.
	 * this should be called once and only once per container at the time of application bootstrap
	 *
	 * @export
	 * @param {Container} iocContainer the container to configure
	 */

	static configureContainer = (): void => {
		// note: there doesnt (yet) seem to be a mature/stable auto-wiring for services.
		// until one is available we will bind services explicitly in this method
		BaseSettingsManager.configureContainer()

		if (BaseSettingsManager.REACT_APP_IS_MAIN_API_MOCKED().value) {
			container
				.bind<ITopNavBarApiClient>('ITopNavBarApiClient')
				.to(MockTopNavBarApiClient)
				.inSingletonScope()
			container
				.bind<ICoverageDiscoveryRequestApiClient>('ICoverageDiscoveryRequestApiClient')
				.to(MockCoverageDiscoveryRequestApiClient)
				.inSingletonScope()
			container
				.bind<ICoverageDiscoveryResponseApiClient>('ICoverageDiscoveryResponseApiClient')
				.to(MockCoverageDiscoveryResponseApiClient)
				.inSingletonScope()
			container
				.bind<ICoverageDiscoveryTransactionApiClient>('ICoverageDiscoveryTransactionApiClient')
				.to(MockCoverageDiscoveryTransactionApiClient)
				.inSingletonScope()
			container
				.bind<IStateCodeApiClient>('IStateCodeApiClient')
				.to(MockStateCodeApiClient)
				.inSingletonScope()
			container
				.bind<IRequestHistoryApiClient>('IRequestHistoryApiClient')
				.to(MockRequestHistoryApiClient)
				.inSingletonScope()
			container
				.bind<IBatchesApiClient>('IBatchesApiClient')
				.to(MockBatchesApiClient)
				.inSingletonScope()
			container
				.bind<IAccountFacilityApiClient>('IAccountFacilityApiClient')
				.to(MockAccountFacilityApiClient)
				.inSingletonScope()
			container
				.bind<IPayersApiClient>('IPayersApiClient')
				.to(MockPayersApiClient)
				.inSingletonScope()
			container
				.bind<ITransactionStatusApiClient>('ITransactionStatusApiClient')
				.to(MockTransactionStatusApiClient)
				.inSingletonScope()
			container
				.bind<IEligibilityStatusApiClient>('IEligibilityStatusApiClient')
				.to(MockEligibilityStatusApiClient)
				.inSingletonScope()
			container
				.bind<IImportBatchUIApiClient>('IImportBatchUIApiClient')
				.to(MockImportBatchUIApiClient)
				.inSingletonScope()
			container
				.bind<IExportRequestApiClient>('IExportRequestApiClient')
				.to(MockExportRequestApiClient)
				.inSingletonScope()
			container
				.bind<IViewReportApiClient>('IViewReportApiClient')
				.to(MockViewReportApiClient)
				.inSingletonScope()
		} else {
			container
				.bind<ITopNavBarApiClient>('ITopNavBarApiClient')
				.to(TopNavBarApiClient)
				.inSingletonScope()
			container
				.bind<ICoverageDiscoveryRequestApiClient>('ICoverageDiscoveryRequestApiClient')
				.to(CoverageDiscoveryRequestApiClient)
				.inSingletonScope()
			container
				.bind<ICoverageDiscoveryResponseApiClient>('ICoverageDiscoveryResponseApiClient')
				.to(CoverageDiscoveryResponseApiClient)
				.inSingletonScope()
			container
				.bind<ICoverageDiscoveryTransactionApiClient>('ICoverageDiscoveryTransactionApiClient')
				.to(CoverageDiscoveryTransactionApiClient)
				.inSingletonScope()
			container
				.bind<IStateCodeApiClient>('IStateCodeApiClient')
				.to(StateCodeApiClient)
				.inSingletonScope()
			container
				.bind<IRequestHistoryApiClient>('IRequestHistoryApiClient')
				.to(RequestHistoryApiClient)
				.inSingletonScope()
			container
				.bind<IBatchesApiClient>('IBatchesApiClient')
				.to(BatchesApiClient)
				.inSingletonScope()
			container
				.bind<IAccountFacilityApiClient>('IAccountFacilityApiClient')
				.to(AccountFacilityApiClient)
				.inSingletonScope()
			container
				.bind<IPayersApiClient>('IPayersApiClient')
				.to(PayersApiClient)
				.inSingletonScope()
			container
				.bind<ITransactionStatusApiClient>('ITransactionStatusApiClient')
				.to(TransactionStatusApiClient)
				.inSingletonScope()
			container
				.bind<IEligibilityStatusApiClient>('IEligibilityStatusApiClient')
				.to(EligibilityStatusApiClient)
				.inSingletonScope()
			container
				.bind<IImportBatchUIApiClient>('IImportBatchUIApiClient')
				.to(ImportBatchUIClient)
				.inSingletonScope()
			container
				.bind<IExportRequestApiClient>('IExportRequestApiClient')
				.to(ExportRequestApiClient)
				.inSingletonScope()
			container
				.bind<IViewReportApiClient>('IViewReportApiClient')
				.to(ViewReportApiClient)
				.inSingletonScope()
		}
		container
			.bind<ICoverageDiscoveryTransactionStateManager>('ICoverageDiscoveryTransactionStateManager')
			.to(CoverageDiscoveryTransactionStateManager)
			.inSingletonScope()
		container
			.bind<ICoverageDiscoveryResponseStateManager>('ICoverageDiscoveryResponseStateManager')
			.to(CoverageDiscoveryResponseStateManager)
			.inSingletonScope()
		container
			.bind<ICoverageDiscoveryRequestStateManager>('ICoverageDiscoveryRequestStateManager')
			.to(CoverageDiscoveryRequestStateManager)
			.inSingletonScope()
		container
			.bind<IRequestHistoryStateManager>('IRequestHistoryStateManager')
			.to(RequestHistoryStateManager)
			.inSingletonScope()
		container
			.bind<IBatchesStateManager>('IBatchesStateManager')
			.to(BatchesStateManager)
			.inSingletonScope()
		container
			.bind<IAccountFacilityGridStateManager>('IAccountFacilityGridStateManager')
			.to(AccountFacilityStateManager)
			.inSingletonScope()
		container
			.bind<IImportBatchUIStateManager>('IImportBatchUIStateManager')
			.to(ImportBatchUIStateManager)
			.inSingletonScope()
	}

	static setProductLocalStorage(currentSessionJwt: string | null): void {
		localStorage.removeItem('Permission_' + SettingsManager.PRODUCT_PERMISSION_FOR_FACILITY_CONFIGURATION)

		let tokenVal: any
		if (currentSessionJwt !== null) {
			tokenVal = jwtDecode(currentSessionJwt)

			if (tokenVal !== null && typeof tokenVal !== 'undefined') {
				if (tokenVal.permissions !== null && typeof tokenVal.permissions !== 'undefined') {
					localStorage.setItem('Permission_' + SettingsManager.PRODUCT_PERMISSION_FOR_FACILITY_CONFIGURATION, (tokenVal.permissions.indexOf(SettingsManager.PRODUCT_PERMISSION_FOR_FACILITY_CONFIGURATION) > -1).toString())
				}
			}
		}
	}
}
